import React from 'react'

/**
 * Which cut the chart on THIS slide is currently showing.
 *
 * A slide's narrative — headline, body copy, stat row, quote card — is a sibling of
 * the chart, not a descendant, so it never learned that a breakdown was on and kept
 * printing whole-sample figures beside a segmented chart. A Likert header read
 * "52.1% favorable" next to a chart reading 100% for Male.
 *
 * The scope is deliberately PER SLIDE. The chart-segment provider is deck-wide and
 * shared by every slide, so publishing the selection through it would leak one
 * slide's cut into another slide's copy.
 */
export interface ChartSegmentSelectionState {
  isSegmented: boolean
  axisId?: string
  axisLabel?: string
}

/** A slide that never opts in behaves exactly as it did before. */
const WHOLE_SAMPLE: ChartSegmentSelectionState = { isSegmented: false }

const ChartSegmentSelectionContext =
  React.createContext<ChartSegmentSelectionState>(WHOLE_SAMPLE)

type PublishChartSegmentSelection = (next: ChartSegmentSelectionState) => void

const NOOP_PUBLISH: PublishChartSegmentSelection = () => {}

const ChartSegmentSelectionPublishContext =
  React.createContext<PublishChartSegmentSelection>(NOOP_PUBLISH)

/**
 * Wraps one slide, so that the chart and the copy beside it agree about who they
 * are describing.
 *
 * The chart sits INSIDE the region this has to cover, so it cannot be the provider
 * and cannot be handed a `value` from above; it reports its cut upward through
 * `usePublishChartSegmentSelection` instead, and the narrative reads it back down.
 * Pass `value` to drive the scope explicitly (tests, and any caller that already
 * knows the cut); omit it to let the chart inside publish its own.
 */
export function ChartSegmentSelectionScope(props: {
  value?: ChartSegmentSelectionState
  children: React.ReactNode
}) {
  const { value, children } = props
  const [published, setPublished] = React.useState<ChartSegmentSelectionState>(WHOLE_SAMPLE)
  const resolved = value ?? published
  // Identity-stable so a narrative sibling does not re-render on every chart tick.
  const state = React.useMemo(
    () => ({
      isSegmented: resolved.isSegmented,
      axisId: resolved.axisId,
      axisLabel: resolved.axisLabel,
    }),
    [resolved.isSegmented, resolved.axisId, resolved.axisLabel],
  )
  // Value equality, not identity: the chart re-renders on every fetch tick and
  // republishes, and an identity-new state here would re-render the narrative and
  // re-fire the publisher's effect, forever.
  const publish = React.useCallback<PublishChartSegmentSelection>((next) => {
    setPublished((current) =>
      current.isSegmented === next.isSegmented &&
      current.axisId === next.axisId &&
      current.axisLabel === next.axisLabel
        ? current
        : { isSegmented: next.isSegmented, axisId: next.axisId, axisLabel: next.axisLabel },
    )
  }, [])
  return (
    <ChartSegmentSelectionPublishContext.Provider value={publish}>
      <ChartSegmentSelectionContext.Provider value={state}>
        {children}
      </ChartSegmentSelectionContext.Provider>
    </ChartSegmentSelectionPublishContext.Provider>
  )
}

export function useChartSegmentSelection(): ChartSegmentSelectionState {
  return React.useContext(ChartSegmentSelectionContext)
}

/**
 * How the chart tells the copy beside it what it is showing. The chart is a
 * descendant of the scope, so it reports upward through this rather than through a
 * prop. Outside a scope it is a no-op, so an unwrapped chart is unchanged.
 *
 * The caller owns two things: publish `{ isSegmented: false }` whenever the chart is
 * NOT showing a subset's numbers (no cut, loading, error, empty cut), and reset to
 * that on unmount, so a slide scrolled out of the deck does not leave its copy
 * disclaimed.
 */
export function usePublishChartSegmentSelection(): PublishChartSegmentSelection {
  return React.useContext(ChartSegmentSelectionPublishContext)
}

/**
 * Kept as a no-op so existing slide mounts stay valid. A cut used to stamp "Total
 * sample" on the narrative; that disclaimer is gone — the chips on the chart are
 * the only extra chrome a filter is allowed to add.
 */
export function TotalSampleTag(_props: { style?: React.CSSProperties }) {
  return null
}
